import React from 'react';
import { motion } from 'framer-motion';
import { FaBeer, FaHandshake, FaGlobe, FaUsers, FaHeart, FaTrophy, FaClock, FaMapMarkerAlt, FaPhone, FaEnvelope, FaBriefcase } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import './Culture.css';

function Culture() {
  const navigate = useNavigate();

  const handleGetInTouch = () => {
    navigate('/');
    // Use setTimeout to ensure the home page loads before scrolling
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleViewServices = () => {
    navigate('/');
    // Use setTimeout to ensure the home page loads before scrolling
    setTimeout(() => {
      const element = document.getElementById('services');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="culture-page">
      {/* Navigation */}
      <motion.nav
        className="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="nav-container">
          <Link to="/" className="logo">
            <FaBriefcase className="logo-icon" />
            <span>Talent Consulting PSA</span>
          </Link>

          <div className="nav-menu">
            <Link to="/" className="nav-link">Home</Link>
            <Link to="/#about" className="nav-link">About</Link>
            <Link to="/#services" className="nav-link">Services</Link>
            <Link to="/culture" className="nav-link active">Culture</Link>
            <Link to="/#contact" className="nav-link">Contact</Link>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="culture-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="hero-content"
          >
            <div className="hero-text">
              <h1>Our Culture</h1>
              <p className="hero-subtitle">Where Work Meets Community</p>
              <p className="hero-description">
                Building lasting connections over craft beer, creating an environment where
                colleagues become friends and clients become family.
              </p>
            </div>
            <div className="hero-image">
              <img src="/drinking-beer-1.webp" alt="Team enjoying beer together" className="beer-image" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Culture Story */}
      <section className="culture-story">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="story-content"
          >
            <h2>More Than Just Business</h2>
            <p>
              At Talent Consulting PSA, we believe that the best ideas and strongest partnerships
              are forged over a cold beer. Our unique culture combines professional excellence
              with genuine human connection, creating an environment where colleagues become friends
              and clients become part of our extended family.
            </p>
            <p>
              Founded by Andrii Lebedynets in Piotrków Trybunalski, Poland, we've created a
              workplace that celebrates both our Polish heritage and international outlook.
              Our restaurant serves as more than just a business venue—it's the heart of our
              community, where work and life seamlessly blend.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Culture Features */}
      <section className="culture-features">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <h2>What Makes Us Different</h2>
            <p>Our unique approach to work, life, and community</p>
          </motion.div>

          <div className="features-grid">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
              className="feature-card"
            >
              <div className="feature-icon">
                <FaBeer />
              </div>
              <h3>Friday Beer Sessions</h3>
              <p>
                Every Friday, we gather at our restaurant to unwind, share stories, and celebrate
                the week's achievements. It's where work discussions flow naturally into personal
                connections and where the best ideas often emerge.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="feature-card"
            >
              <div className="feature-icon">
                <FaHandshake />
              </div>
              <h3>Client Hospitality</h3>
              <p>
                We extend our warm hospitality to clients, offering them a taste of our culture.
                Many business deals have been sealed over a shared pint and authentic conversation,
                creating lasting partnerships built on trust and mutual respect.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="feature-card"
            >
              <div className="feature-icon">
                <FaGlobe />
              </div>
              <h3>International Flavor</h3>
              <p>
                Our diverse team brings global perspectives to every gathering. From Polish traditions
                to international customs, we celebrate the rich tapestry of our multicultural team
                and the unique insights each member brings.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="feature-card"
            >
              <div className="feature-icon">
                <FaUsers />
              </div>
              <h3>Community First</h3>
              <p>
                We believe that strong communities create strong businesses. Our team members
                support each other not just professionally, but personally, creating a workplace
                where everyone feels valued and supported.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              viewport={{ once: true }}
              className="feature-card"
            >
              <div className="feature-icon">
                <FaHeart />
              </div>
              <h3>Work-Life Integration</h3>
              <p>
                We don't believe in work-life balance—we believe in work-life integration.
                Our culture allows team members to be their authentic selves while delivering
                exceptional results for our clients.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              className="feature-card"
            >
              <div className="feature-icon">
                <FaTrophy />
              </div>
              <h3>Celebrating Success</h3>
              <p>
                Every milestone, big or small, deserves recognition. We celebrate achievements
                together, whether it's completing a major project or simply making it through
                a challenging week. Success tastes better when shared.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Beer Culture Deep Dive */}
      <section className="beer-culture">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="beer-content"
          >
            <div className="beer-text">
              <h2>The Art of Beer & Business</h2>
              <p>
                Our restaurant isn't just a place to eat and drink—it's where business relationships
                are nurtured and ideas are born. The relaxed atmosphere of sharing a beer creates
                an environment where people feel comfortable sharing their thoughts, concerns, and
                innovative ideas.
              </p>
              <p>
                We offer a carefully curated selection of craft beers, from local Polish breweries
                to international favorites. Each beer tells a story, just like each of our team
                members and clients. The diversity of our beer selection reflects the diversity
                of our team and the global nature of our business.
              </p>
            </div>
            <div className="beer-stats">
              <div className="stat-card">
                <FaBeer className="stat-icon" />
                <h3>15+</h3>
                <p>Beer Varieties</p>
              </div>
              <div className="stat-card">
                <FaClock className="stat-icon" />
                <h3>50+</h3>
                <p>Team Gatherings</p>
              </div>
              <div className="stat-card">
                <FaHandshake className="stat-icon" />
                <h3>100+</h3>
                <p>Client Meetings</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Team Beer Moments */}
      <section className="team-beer-moments">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="moments-content"
          >
            <div className="moments-text">
              <h2>Real Moments, Real Connections</h2>
              <p>
                Every Friday evening, our team gathers around the table, beers in hand,
                sharing not just drinks but stories, challenges, and victories. These
                moments capture the essence of who we are—professionals who believe
                that the best solutions come from relaxed, authentic conversations.
              </p>
              <p>
                Whether it's celebrating a successful project completion, brainstorming
                solutions for a client challenge, or simply unwinding after a busy week,
                our beer culture creates bonds that extend far beyond the workplace.
                It's in these moments that we truly understand each other and our clients.
              </p>
            </div>
            <div className="moments-image">
              <img src="/drinking-beer-2.png" alt="Team celebrating with beer" className="team-beer-image" />
              <div className="image-overlay">
                <div className="overlay-content">
                  <FaBeer className="overlay-icon" />
                  <h3>Friday Tradition</h3>
                  <p>Where work meets celebration</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Team Values */}
      <section className="team-values">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="values-content"
          >
            <h2>Our Core Values</h2>
            <div className="values-grid">
              <div className="value-item">
                <h4>Authenticity</h4>
                <p>We believe in being genuine in all our interactions, both with colleagues and clients.</p>
              </div>
              <div className="value-item">
                <h4>Community</h4>
                <p>Building strong relationships is at the heart of everything we do.</p>
              </div>
              <div className="value-item">
                <h4>Excellence</h4>
                <p>We strive for the highest quality in both our work and our relationships.</p>
              </div>
              <div className="value-item">
                <h4>Innovation</h4>
                <p>Great ideas often come from relaxed, informal conversations over a beer.</p>
              </div>
              <div className="value-item">
                <h4>Respect</h4>
                <p>We value diverse perspectives and create an inclusive environment for everyone.</p>
              </div>
              <div className="value-item">
                <h4>Fun</h4>
                <p>Work should be enjoyable, and we believe in celebrating both successes and challenges.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Join Our Culture */}
      <section className="join-culture">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="join-content"
          >
            <h2>Join Our Culture</h2>
            <p>
              Interested in becoming part of our unique culture? Whether you're looking for
              career opportunities or want to experience our hospitality as a client,
              we'd love to share a beer and get to know you.
            </p>
            <div className="join-actions">
              <motion.button
                className="cta-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleGetInTouch}
              >
                Get In Touch
              </motion.button>
              <motion.button
                className="secondary-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleViewServices}
              >
                View Our Services
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="culture-contact">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="contact-info"
          >
            <h3>Visit Our Restaurant</h3>
            <div className="contact-details">
              <div className="contact-item">
                <FaMapMarkerAlt className="contact-icon" />
                <div>
                  <h4>Address</h4>
                  <p>ul. Szewska 2, 97-300<br />Piotrków Trybunalski, Poland</p>
                </div>
              </div>
              <div className="contact-item">
                <FaPhone className="contact-icon" />
                <div>
                  <h4>Phone</h4>
                  <p>+48 459 569 723</p>
                </div>
              </div>
              <div className="contact-item">
                <FaEnvelope className="contact-icon" />
                <div>
                  <h4>Email</h4>
                  <p>andrii.lebedynets@talentconsulting.online</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Culture;
